/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  DatePicker,
  Descriptions,
  Input,
  Message,
  Modal,
  Select,
  Space,
  Table,
  TableColumnProps,
  Typography
} from '@arco-design/web-react';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { get, post } from '../requestNew';
import PhoneCell from '../components/PhoneCell';
import type { PayOrderItem } from './types';
import './index.scss';

const Option = Select.Option;
const RangePicker = DatePicker.RangePicker;

const orderStatusFilterOptions = [
  { label: '全部', value: '' },
  { label: '支付成功', value: 'SUCCESS' },
  { label: '未支付', value: 'NOTPAY' },
  { label: '转入退款', value: 'REFUND' },
  { label: '已关闭', value: 'CLOSED' }
];

const orderStatusUpdateOptions = orderStatusFilterOptions.filter(o => o.value !== '');

const orderStatusLabelByValue: Record<string, string> = Object.fromEntries(
  orderStatusUpdateOptions.map(({ value, label }) => [value, label])
);

const orderTypeOptions = [
  { label: '全部', value: '' },
  { label: '报名订单', value: '1' },
  { label: '活动订单', value: '2' },
  { label: '会员订单', value: '3' },
  { label: '复盘会员订单', value: '4' }
];

const orderTypeLabelByValue: Record<string, string> = {
  '1': '报名订单',
  '2': '活动订单',
  '3': '会员订单',
  '4': '复盘会员订单'
};

const payTypeOptions = [
  { label: '全部', value: '' },
  { label: '微信', value: '1' },
  { label: '非微信/后台', value: '2' }
];

const payChannelOptions = [
  { label: '全部', value: '' },
  { label: '小程序', value: '1' },
  { label: '复利笔记APP', value: '2' }
];

const payChannelLabelByValue: Record<string, string> = {
  '1': '小程序',
  '2': '复利笔记APP',
  '3': '微信虚拟支付',
  '4': '苹果内购IAP'
};

const isValidOptions = [
  { label: '全部', value: '' },
  { label: '有效', value: '1' },
  { label: '无效', value: '0' }
];

function isRecordInvalid(record: PayOrderItem): boolean {
  const v = record?.isValid;
  if (v === false || v === 0) return true;
  if (typeof v === 'string' && v.trim() === '0') return true;
  return false;
}

type FilterState = {
  orderId: string;
  prePayId: string;
  openId: string;
  phone: string;
  gameSession: string;
  orderStatus: string;
  orderType: string;
  isValid: string;
  payType: string;
  payChannel: string;
  payDateStart: string;
  payDateEnd: string;
};

const emptyFilters: FilterState = {
  orderId: '',
  prePayId: '',
  openId: '',
  phone: '',
  gameSession: '',
  orderStatus: '',
  orderType: '',
  isValid: '',
  payType: '',
  payChannel: '',
  payDateStart: '',
  payDateEnd: ''
};

function PayOrder() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<FilterState>(emptyFilters);
  const [sessionOptions, setSessionOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [dataList, setDataList] = useState<PayOrderItem[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const isInitialMount = useRef(true);

  const [detailVisible, setDetailVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<PayOrderItem | null>(null);

  const [statusVisible, setStatusVisible] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [editingOrder, setEditingOrder] = useState<PayOrderItem | null>(null);
  const [nextStatus, setNextStatus] = useState('');

  const buildParams = (page: number, size: number, f: FilterState) => {
    const params: Record<string, string | number> = {
      pageNum: page,
      pageSize: size
    };
    if (f.orderId.trim()) params.orderId = f.orderId.trim();
    if (f.prePayId.trim()) params.prePayId = f.prePayId.trim();
    if (f.openId.trim()) params.openId = f.openId.trim();
    if (f.phone.trim()) params.phone = f.phone.trim();
    if (f.gameSession) params.gameSession = f.gameSession;
    if (f.orderStatus) params.orderStatus = f.orderStatus;
    if (f.orderType) params.orderType = f.orderType;
    if (f.isValid) params.isValid = Number(f.isValid);
    if (f.payType) params.payType = Number(f.payType);
    if (f.payChannel) params.payChannel = Number(f.payChannel);
    if (f.payDateStart) params.payDateStart = f.payDateStart;
    if (f.payDateEnd) params.payDateEnd = f.payDateEnd;
    return params;
  };

  const getList = (opts?: { pageNum?: number; pageSize?: number; filters?: FilterState; needLoading?: boolean }) => {
    const nextPage = opts?.pageNum ?? pageNum;
    const nextSize = opts?.pageSize ?? pageSize;
    const nextFilters = opts?.filters ?? filters;
    if (opts?.needLoading !== false) {
      setLoading(true);
    }
    get('/sys/umsPayOrder/list', buildParams(nextPage, nextSize, nextFilters))
      .then((res: any) => {
        const { code, data, message } = res || {};
        if (code === 200) {
          setDataList(data?.list ?? data?.records ?? []);
          setTotal(data?.total ?? 0);
        } else {
          Message.error(message || '查询失败');
        }
      })
      .catch(() => Message.error('网络错误'))
      .finally(() => setLoading(false));
  };

  const patchFilter = (patch: Partial<FilterState>, autoQuery = false) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    if (autoQuery) {
      setPageNum(1);
      getList({ pageNum: 1, filters: next });
    }
  };

  const handleQuery = () => {
    setPageNum(1);
    getList({ pageNum: 1 });
  };

  const handleReset = () => {
    setFilters(emptyFilters);
    setPageNum(1);
    getList({ pageNum: 1, filters: emptyFilters });
  };

  const openDetail = (record: PayOrderItem) => {
    if (record?.id == null) {
      Message.error('订单 ID 不存在');
      return;
    }
    setDetailVisible(true);
    setDetailLoading(true);
    setDetail(null);
    get('/sys/umsPayOrder/detail', { id: record.id })
      .then((res: any) => {
        if (res?.code === 200) {
          setDetail(res.data || null);
        } else {
          Message.error(res?.message || '查询详情失败');
          setDetailVisible(false);
        }
      })
      .catch(() => {
        Message.error('网络错误');
        setDetailVisible(false);
      })
      .finally(() => setDetailLoading(false));
  };

  const openUpdateStatus = (record: PayOrderItem) => {
    setEditingOrder(record);
    setNextStatus(record.orderStatus || 'SUCCESS');
    setStatusVisible(true);
  };

  const submitUpdateStatus = () => {
    if (!editingOrder?.id) {
      Message.error('订单 ID 不存在');
      return;
    }
    if (!nextStatus) {
      Message.error('请选择订单状态');
      return;
    }
    setStatusLoading(true);
    post('/sys/umsPayOrder/updateStatus', {
      id: editingOrder.id,
      orderStatus: nextStatus,
      syncQualification: true
    })
      .then((res: any) => {
        if (res?.code === 200) {
          Message.success('更新成功');
          setStatusVisible(false);
          getList();
        } else {
          Message.error(res?.message || '更新失败');
        }
      })
      .catch(() => Message.error('网络错误'))
      .finally(() => setStatusLoading(false));
  };

  const handleLogicDelete = (record: PayOrderItem) => {
    if (isRecordInvalid(record)) return;
    if (record?.id == null) {
      Message.error('订单 ID 不存在');
      return;
    }
    Modal.confirm({
      title: '提示',
      content: '确定逻辑删除该订单吗？将置为无效并标记为退款，同时同步报名资格支付状态。',
      focusLock: true,
      onOk: () =>
        post(`/sys/umsPayOrder/logicDelete/${record.id}`, {}).then((res: any) => {
          if (res?.code === 200) {
            Message.success('删除成功');
            getList();
          } else {
            Message.error(res?.message || '删除失败');
          }
        })
    });
  };

  const columns: TableColumnProps<PayOrderItem>[] = [
    {
      title: '订单号',
      dataIndex: 'orderId',
      width: 200,
      render: v => v || '-'
    },
    {
      title: '微信预支付ID',
      dataIndex: 'prePayId',
      width: 180,
      render: v => v || '-'
    },
    {
      title: '微信交易号',
      dataIndex: 'transactionId',
      width: 220,
      render: v =>
        v ? (
          <Typography.Text copyable style={{ margin: 0 }}>
            {v}
          </Typography.Text>
        ) : (
          '-'
        )
    },
    {
      title: '微信商户单号',
      dataIndex: 'wxMchOrderNo',
      width: 200,
      render: v =>
        v ? (
          <Typography.Text copyable style={{ margin: 0 }}>
            {v}
          </Typography.Text>
        ) : (
          '-'
        )
    },
    {
      title: 'openId',
      dataIndex: 'openId',
      width: 200,
      render: v =>
        v ? (
          <Typography.Text copyable style={{ margin: 0 }}>
            {v}
          </Typography.Text>
        ) : (
          '-'
        )
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      width: 160,
      render: text => <PhoneCell phone={text} />
    },
    {
      title: '赛季',
      dataIndex: 'gameSession',
      width: 120,
      render: v => v || '-'
    },
    {
      title: '订单类型',
      dataIndex: 'orderType',
      width: 120,
      render: v => orderTypeLabelByValue[String(v ?? '')] || v || '-'
    },
    {
      title: '支付渠道',
      dataIndex: 'payType',
      width: 100,
      render: v => (v === 1 ? '微信' : v === 2 ? '非微信/后台' : '-')
    },
    {
      title: '支付端',
      dataIndex: 'payChannel',
      width: 120,
      render: v => payChannelLabelByValue[String(v ?? '')] || '-'
    },
    {
      title: '支付状态',
      dataIndex: 'orderStatus',
      width: 110,
      render: text => {
        const status = text == null || text === '' ? '' : String(text);
        if (!status) return '-';
        const label = orderStatusLabelByValue[status] ?? status;
        const color = status === 'SUCCESS' ? '#52c41a' : status === 'REFUND' ? '#f53f3f' : undefined;
        return <span style={color ? { color } : undefined}>{label}</span>;
      }
    },
    {
      title: '金额',
      dataIndex: 'payPrice',
      width: 100,
      render: text => (text != null && text !== '' ? `¥${(Number(text) / 100).toFixed(2)}` : '-')
    },
    {
      title: '支付日',
      dataIndex: 'payDate',
      width: 120,
      render: v => v || '-'
    },
    {
      title: '是否有效',
      dataIndex: 'isValid',
      width: 90,
      render: (_text, record) =>
        isRecordInvalid(record) ? (
          <span style={{ color: 'var(--color-text-3)' }}>无效</span>
        ) : (
          <span style={{ color: '#52c41a' }}>有效</span>
        )
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 170,
      render: text => (text && dayjs(text).isValid() ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-')
    },
    {
      title: '操作',
      dataIndex: 'operation',
      width: 200,
      fixed: 'right',
      render: (_text, record) => (
        <Space size={4}>
          <Button type="text" size="small" onClick={() => openDetail(record)}>
            详情
          </Button>
          <Button type="text" size="small" onClick={() => openUpdateStatus(record)}>
            改状态
          </Button>
          <Button
            type="text"
            status="danger"
            size="small"
            disabled={isRecordInvalid(record)}
            onClick={() => handleLogicDelete(record)}
          >
            删除
          </Button>
        </Space>
      )
    }
  ];

  useEffect(() => {
    get('/gameConfig/getUmsGameConfigList').then((res: any) => {
      if (res?.data) {
        const list = (res.data || []).map((item: any) => ({
          label: item.gameSession,
          value: item.gameSession
        }));
        setSessionOptions(list);
      }
    });
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      const orderId = (searchParams.get('orderId') || '').trim();
      if (orderId) {
        const next = { ...emptyFilters, orderId };
        setFilters(next);
        getList({ pageNum: 1, filters: next });
        return;
      }
      getList({ pageNum: 1 });
      return;
    }
    getList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNum, pageSize]);

  return (
    <div className="pay-order table-flex">
      <Space style={{ flexWrap: 'wrap' }}>
        <div className="filter-item">
          <Typography.Text>订单号:</Typography.Text>
          <Input
            value={filters.orderId}
            onChange={v => patchFilter({ orderId: v })}
            style={{ width: 180 }}
            allowClear
            placeholder="模糊搜索"
          />
        </div>
        <div className="filter-item">
          <Typography.Text>预支付ID:</Typography.Text>
          <Input
            value={filters.prePayId}
            onChange={v => patchFilter({ prePayId: v })}
            style={{ width: 180 }}
            allowClear
            placeholder="模糊搜索"
          />
        </div>
        <div className="filter-item">
          <Typography.Text>openId:</Typography.Text>
          <Input
            value={filters.openId}
            onChange={v => patchFilter({ openId: v })}
            style={{ width: 180 }}
            allowClear
          />
        </div>
        <div className="filter-item">
          <Typography.Text>手机号:</Typography.Text>
          <Input
            value={filters.phone}
            onChange={v => patchFilter({ phone: v })}
            style={{ width: 150 }}
            allowClear
          />
        </div>
        <div className="filter-item">
          <Typography.Text>赛季:</Typography.Text>
          <Select
            value={filters.gameSession || undefined}
            onChange={v => patchFilter({ gameSession: v || '' }, true)}
            style={{ width: 160 }}
            allowClear
            placeholder="全部"
          >
            {sessionOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </div>
        <div className="filter-item">
          <Typography.Text>支付状态:</Typography.Text>
          <Select
            value={filters.orderStatus}
            onChange={v => patchFilter({ orderStatus: v }, true)}
            style={{ width: 140 }}
          >
            {orderStatusFilterOptions.map(option => (
              <Option key={option.label + option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </div>
        <div className="filter-item">
          <Typography.Text>订单类型:</Typography.Text>
          <Select
            value={filters.orderType}
            onChange={v => patchFilter({ orderType: v }, true)}
            style={{ width: 150 }}
          >
            {orderTypeOptions.map(option => (
              <Option key={option.label + option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </div>
        <div className="filter-item">
          <Typography.Text>支付渠道:</Typography.Text>
          <Select
            value={filters.payType}
            onChange={v => patchFilter({ payType: v }, true)}
            style={{ width: 130 }}
          >
            {payTypeOptions.map(option => (
              <Option key={option.label + option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </div>
        <div className="filter-item">
          <Typography.Text>支付端:</Typography.Text>
          <Select
            value={filters.payChannel}
            onChange={v => patchFilter({ payChannel: v }, true)}
            style={{ width: 140 }}
          >
            {payChannelOptions.map(option => (
              <Option key={option.label + option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </div>
        <div className="filter-item">
          <Typography.Text>是否有效:</Typography.Text>
          <Select
            value={filters.isValid}
            onChange={v => patchFilter({ isValid: v }, true)}
            style={{ width: 110 }}
          >
            {isValidOptions.map(option => (
              <Option key={option.label + option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </div>
        <div className="filter-item">
          <Typography.Text>支付日:</Typography.Text>
          <RangePicker
            style={{ width: 260 }}
            format="YYYY-MM-DD"
            value={
              filters.payDateStart && filters.payDateEnd
                ? [filters.payDateStart, filters.payDateEnd]
                : undefined
            }
            onChange={dateString => {
              const start = dateString?.[0] || '';
              const end = dateString?.[1] || '';
              patchFilter({ payDateStart: start, payDateEnd: end }, true);
            }}
          />
        </div>
        <Button className="filter-item" type="primary" onClick={handleQuery}>
          查询
        </Button>
        <Button className="filter-item" type="outline" onClick={handleReset}>
          重置
        </Button>
      </Space>

      <div className="table-content">
        <Table
          loading={loading}
          columns={columns}
          data={dataList}
          pagination={{
            current: pageNum,
            pageSize,
            total,
            sizeCanChange: true,
            sizeOptions: [20, 50, 100],
            showTotal: true,
            onChange: (page, size) => {
              setPageNum(page);
              if (size && size !== pageSize) {
                setPageSize(size);
              }
            }
          }}
          rowKey="id"
          className="table-scroll"
          scroll={{ y: '100%', x: '100%' }}
        />
      </div>

      <Modal
        title="订单详情"
        visible={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        style={{ width: 760 }}
        unmountOnExit
      >
        {detailLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>加载中...</div>
        ) : detail ? (
          <>
            <Descriptions
              column={2}
              data={[
                { label: 'ID', value: detail.id },
                { label: '订单号', value: detail.orderId || '-' },
                { label: '预支付ID', value: detail.prePayId || '-' },
                { label: '微信交易号', value: detail.transactionId || '-' },
                { label: '微信商户单号', value: detail.wxMchOrderNo || '-' },
                {
                  label: '订单类型',
                  value: orderTypeLabelByValue[String(detail.orderType ?? '')] || detail.orderType || '-'
                },
                {
                  label: '支付状态',
                  value: orderStatusLabelByValue[String(detail.orderStatus ?? '')] || detail.orderStatus || '-'
                },
                {
                  label: '支付渠道',
                  value: detail.payType === 1 ? '微信' : detail.payType === 2 ? '非微信/后台' : '-'
                },
                {
                  label: '支付端',
                  value: payChannelLabelByValue[String(detail.payChannel ?? '')] || '-'
                },
                { label: 'openId', value: detail.openId || '-' },
                { label: '手机号', value: <PhoneCell phone={detail.phone} /> },
                { label: '赛季', value: detail.gameSession || '-' },
                {
                  label: '金额',
                  value:
                    detail.payPrice != null ? `¥${(Number(detail.payPrice) / 100).toFixed(2)}` : '-'
                },
                { label: '支付日', value: detail.payDate || '-' },
                {
                  label: '是否有效',
                  value: isRecordInvalid(detail) ? '无效' : '有效'
                },
                {
                  label: '创建时间',
                  value:
                    detail.createTime && dayjs(detail.createTime).isValid()
                      ? dayjs(detail.createTime).format('YYYY-MM-DD HH:mm:ss')
                      : '-'
                },
                {
                  label: '更新时间',
                  value:
                    detail.updateTime && dayjs(detail.updateTime).isValid()
                      ? dayjs(detail.updateTime).format('YYYY-MM-DD HH:mm:ss')
                      : '-'
                }
              ]}
            />
            <div style={{ marginTop: 24, textAlign: 'right' }}>
              <Button type="primary" status="warning">
                处理售后/退款
              </Button>
            </div>
          </>
        ) : (
          <div style={{ padding: 24, textAlign: 'center' }}>暂无数据</div>
        )}
      </Modal>

      <Modal
        title="更新订单状态"
        visible={statusVisible}
        onCancel={() => setStatusVisible(false)}
        onOk={submitUpdateStatus}
        confirmLoading={statusLoading}
        unmountOnExit
      >
        <div style={{ marginBottom: 12 }}>
          订单号：{editingOrder?.orderId || '-'}
        </div>
        <div className="filter-item">
          <Typography.Text>目标状态:</Typography.Text>
          <Select value={nextStatus} onChange={setNextStatus} style={{ width: 220 }}>
            {orderStatusUpdateOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </div>
        <Typography.Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
          置为「转入退款」时会将订单设为无效，并默认同步报名资格支付状态。
        </Typography.Text>
      </Modal>
    </div>
  );
}

export default PayOrder;
